import React, { useEffect, useState } from 'react';
import { Text, TouchableOpacity, View } from 'react-native';
import { FontAwesome5 } from '@expo/vector-icons';

import { getProductFields, deleteProductField } from '../../../api/Product';
import { msgStr } from '../../../common/constants/Message';
import { TextMediumSize } from '../../../common/constants/Fonts';
import { useAlertModal, useConfirmModal } from '../../../common/hooks';
import { BasicLayout, CommonContainer } from '../../../common/components/CustomLayout';
import { BOHButton, BOHToolbar } from '../../../common/components/bohtoolbar';
import { BOHTBody, BOHTD, BOHTDIconBox, BOHTH, BOHTHead, BOHTR, BOHTable } from '../../../common/components/bohtable';
import InventoryFieldModal from './InventoryFieldModal';

export const FIELD_TYPE_LABELS: Record<string, string> = {
  text: 'Text',
  integer: 'Whole Number',
  decimal: 'Decimal Number',
  date: 'Date',
  dropdown: 'Dropdown',
  multiline: 'MultiLine Description',
  url: 'URL',
};

const parseOptions = (options: any): string[] => {
  if (!options) return [];
  try {
    const parsed = JSON.parse(options);
    return Array.isArray(parsed) ? parsed.map(String) : [];
  } catch {
    return [];
  }
};

const InventoryFields = ({ navigation, openInventory }) => {
  const { showAlert } = useAlertModal();
  const { showConfirm } = useConfirmModal();

  const [tableData, setTableData] = useState([]);
  const [updateTrigger, setUpdateTrigger] = useState(true);
  const [isAddModalVisible, setAddModalVisible] = useState(false);
  const [selectedField, setSelectedField] = useState(null);

  const InitialWidths = [50, 200, 140, 150, 160, 200, 60, 60, 50, 50];

  const openAddModal = () => {
    setSelectedField(null);
    setAddModalVisible(true);
  };
  const closeModal = () => {
    setAddModalVisible(false);
    setSelectedField(null);
  };
  const editField = (item) => {
    setSelectedField(item);
    setAddModalVisible(true);
  };

  useEffect(() => {
    if (updateTrigger) getTable();
  }, [updateTrigger]);

  const removeField = (item) => {
    showConfirm(`Delete field "${item.label}"? Values stored on products will also be removed.`, () => {
      deleteProductField(item.id, (jsonRes, status) => {
        switch (status) {
          case 200:
            setUpdateTrigger(true);
            showAlert('success', jsonRes.message || 'Field deleted');
            break;
          default:
            if (jsonRes && jsonRes.error) showAlert('error', jsonRes.error);
            else showAlert('error', msgStr('unknownError'));
            break;
        }
      });
    });
  };

  const getTable = () => {
    getProductFields((jsonRes, status) => {
      switch (status) {
        case 200:
          setUpdateTrigger(false);
          setTableData(jsonRes);
          break;
        case 500:
          showAlert('error', msgStr('serverError'));
          break;
        default:
          if (jsonRes && jsonRes.error) showAlert('error', jsonRes.error);
          else showAlert('error', msgStr('unknownError'));
          break;
      }
    });
  };

  const renderTableData = () => {
    const rows = [];
    if (tableData.length > 0) {
      tableData.map((item: any, index: number) => {
        rows.push(
          <BOHTR key={index}>
            <BOHTD width={InitialWidths[0]}>{item.id}</BOHTD>
            <BOHTD width={InitialWidths[1]}>{item.label}</BOHTD>
            <BOHTD width={InitialWidths[2]}>{item.field_key}</BOHTD>
            <BOHTD width={InitialWidths[3]}>{FIELD_TYPE_LABELS[item.field_type] || item.field_type}</BOHTD>
            <BOHTD width={InitialWidths[4]}>{item.default_value || ''}</BOHTD>
            <BOHTD width={InitialWidths[5]}>
              {item.field_type === 'dropdown' ? parseOptions(item.options).join(', ') : ''}
            </BOHTD>
            <BOHTD width={InitialWidths[6]} textAlign={'right'}>{item.sort}</BOHTD>
            <BOHTD width={InitialWidths[7]}>{item.active ? 'Yes' : 'No'}</BOHTD>
            <BOHTDIconBox width={InitialWidths[8]}>
              <TouchableOpacity onPress={() => editField(item)}>
                <FontAwesome5 size={TextMediumSize} name="edit" color="black" />
              </TouchableOpacity>
            </BOHTDIconBox>
            <BOHTDIconBox width={InitialWidths[9]}>
              <TouchableOpacity onPress={() => removeField(item)}>
                <FontAwesome5 size={TextMediumSize} name="times" color="black" />
              </TouchableOpacity>
            </BOHTDIconBox>
          </BOHTR>
        );
      });
    }
    return <>{rows}</>;
  };

  return (
    <BasicLayout
      navigation={navigation}
      goBack={() => {
        openInventory(null);
      }}
      screenName={'Inventory Fields'}
    >
      <CommonContainer>
        <BOHToolbar>
          <BOHButton label={'Add Field'} onPress={openAddModal} />
        </BOHToolbar>
        <View style={{ paddingHorizontal: 10, paddingBottom: 8 }}>
          <Text style={{ color: '#555' }}>
            Custom fields defined here appear as extra columns on the Inventory → Products page
            and can be matched to columns during import.
          </Text>
        </View>
        <BOHTable>
          <BOHTHead>
            <BOHTR>
              <BOHTH width={InitialWidths[0]}>{'ID'}</BOHTH>
              <BOHTH width={InitialWidths[1]}>{'Label'}</BOHTH>
              <BOHTH width={InitialWidths[2]}>{'Key'}</BOHTH>
              <BOHTH width={InitialWidths[3]}>{'Type'}</BOHTH>
              <BOHTH width={InitialWidths[4]}>{'Default'}</BOHTH>
              <BOHTH width={InitialWidths[5]}>{'Options'}</BOHTH>
              <BOHTH width={InitialWidths[6]}>{'Sort'}</BOHTH>
              <BOHTH width={InitialWidths[7]}>{'Active'}</BOHTH>
              <BOHTH width={InitialWidths[8]}>{'Edit'}</BOHTH>
              <BOHTH width={InitialWidths[9]}>{'DEL'}</BOHTH>
            </BOHTR>
          </BOHTHead>
          <BOHTBody>
            {renderTableData()}
          </BOHTBody>
        </BOHTable>
      </CommonContainer>

      <InventoryFieldModal
        isModalVisible={isAddModalVisible}
        Field={selectedField}
        setUpdateTrigger={setUpdateTrigger}
        closeModal={closeModal}
      />
    </BasicLayout>
  );
};

export default InventoryFields;
